import { SidebarMenuButton } from '@Pages/pageRuntime/components/ui/Sidebar';
import { cn } from '@Pages/pageRuntime/lib/utils';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { tabsTriggerVariants } from './tabsTriggerVariants';

type TabsVariant = 'pill' | 'underline' | 'rail';

const TabsVariantContext = React.createContext<TabsVariant>('pill');
// Rail triggers render as sidebar menu buttons, which mark the current item with `isActive`
// rather than Radix's `data-state`, so they need to know the selected value.
const TabsValueContext = React.createContext<string | undefined>(undefined);

export interface TabsProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root> {
  variant?: TabsVariant;
}

const Tabs = ({
  variant = 'pill',
  value,
  defaultValue,
  onValueChange,
  ref,
  ...props
}: TabsProps & { ref?: React.Ref<React.ComponentRef<typeof TabsPrimitive.Root>> }) => {
  const [uncontrolledValue, setUncontrolledValue] = React.useState(defaultValue);
  const pendingValueRef = React.useRef<string | null>(null);
  const handleValueChange = (nextValue: string) => {
    // Radix focuses a pressed trigger before it selects it, and the focus also selects it under
    // automatic activation, so one press reports the same value twice within one task.
    if (pendingValueRef.current === nextValue) {
      return;
    }

    pendingValueRef.current = nextValue;
    queueMicrotask(() => {
      pendingValueRef.current = null;
    });

    setUncontrolledValue(nextValue);
    onValueChange?.(nextValue);
  };

  return (
    <TabsVariantContext.Provider value={variant}>
      <TabsValueContext.Provider value={value ?? uncontrolledValue}>
        <TabsPrimitive.Root
          {...props}
          value={value}
          defaultValue={defaultValue}
          onValueChange={handleValueChange}
          ref={ref}
        />
      </TabsValueContext.Provider>
    </TabsVariantContext.Provider>
  );
};
Tabs.displayName = TabsPrimitive.Root.displayName;

const tabsListVariants = cva('flex flex-nowrap w-max', {
  variants: {
    variant: {
      pill: 'h-9 items-center rounded-lg bg-muted p-1 text-muted-foreground',
      underline: 'items-end justify-start gap-4 rounded-none bg-transparent p-0',
      rail: 'w-max bg-transparent p-2 sm:w-full sm:flex-col'
    }
  },
  defaultVariants: {
    variant: 'pill'
  }
});

const TabsList = ({
  className,
  variant,
  ref,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> & VariantProps<typeof tabsListVariants>) => {
  const contextVariant = React.use(TabsVariantContext);
  const resolvedVariant = variant ?? contextVariant;

  return (
    <div
      className={cn(
        '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden',
        resolvedVariant === 'rail'
          ? 'shrink-0 overflow-x-auto overflow-y-hidden border-b border-border bg-muted/30 sm:h-full sm:min-h-0 sm:w-52 sm:overflow-x-visible sm:overflow-y-auto sm:border-r sm:border-b-0'
          : 'overflow-x-auto overflow-y-hidden',
        resolvedVariant === 'underline' && 'border-b border-input'
      )}
    >
      <TabsPrimitive.List
        ref={ref}
        className={cn(tabsListVariants({ variant: resolvedVariant }), className)}
        {...props}
      />
    </div>
  );
};
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = ({
  className,
  variant,
  value,
  children,
  ref,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger> &
  VariantProps<typeof tabsTriggerVariants>) => {
  const contextVariant = React.use(TabsVariantContext);
  const selectedValue = React.use(TabsValueContext);
  const resolvedVariant = variant ?? contextVariant;

  if (resolvedVariant === 'rail') {
    return (
      <TabsPrimitive.Trigger ref={ref} value={value} asChild {...props}>
        <SidebarMenuButton
          isActive={value === selectedValue}
          className={cn('w-auto shrink-0 justify-between sm:w-full', className)}
        >
          {children}
        </SidebarMenuButton>
      </TabsPrimitive.Trigger>
    );
  }

  return (
    <TabsPrimitive.Trigger
      ref={ref}
      value={value}
      className={cn(tabsTriggerVariants({ variant: resolvedVariant }), className)}
      {...props}
    >
      {children}
    </TabsPrimitive.Trigger>
  );
};
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = ({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-2 ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none',
      className
    )}
    {...props}
  />
);
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsContent, TabsList, TabsTrigger };
